// Path: sec-parser/client/src/pages/BenchmarkPage.jsx
import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Card, Row, Col, Checkbox, Button, Typography, Space,
  Progress, Tag, Spin, Alert, Divider, Statistic, Badge,
  Table, Tooltip, Radio, Segmented,
} from "antd";
import {
  RocketOutlined, TrophyOutlined, LoadingOutlined,
  ClockCircleOutlined, DollarOutlined, CheckOutlined,
  EyeOutlined, LinkOutlined, FileTextOutlined, InfoCircleOutlined,
  SplitCellsOutlined,
} from "@ant-design/icons";
import { getModels, runBenchmark, getBenchmarkRun, getPromptPreview } from "../services/api";
import PromptInspectorModal from "../components/benchmark/PromptInspectorModal";

const { Title, Text } = Typography;

const MODEL_META = {
  claude:   { color: "#8B5CF6", emoji: "🟣", tier: "paid" },
  gpt4o:    { color: "#10A37F", emoji: "🟢", tier: "paid", urlMode: true },
  gemini:   { color: "#4285F4", emoji: "🔵", tier: "free", urlMode: true },
  groq:     { color: "#F55036", emoji: "🔴", tier: "free" },
  grok:     { color: "#111",    emoji: "⚫", tier: "paid" },
  mistral:  { color: "#ff7000", emoji: "🟠", tier: "free" },
  deepseek: { color: "#0066ff", emoji: "🔷", tier: "cheap" },
  cohere:   { color: "#D1B26F", emoji: "🟡", tier: "trial" },
};

const TIER_COLORS = { paid: "purple", free: "green", cheap: "blue", trial: "orange" };

export default function BenchmarkPage() {
  const { filingId } = useParams();
  const navigate     = useNavigate();

  const [models, setModels]               = useState([]);
  const [selected, setSelected]           = useState([]);
  const [runId, setRunId]                 = useState(null);
  const [runData, setRunData]             = useState(null);
  const [phase, setPhase]                 = useState("configure");
  const [inspectorOpen, setInspectorOpen] = useState(false);
  const [preview, setPreview]             = useState(null);
  const [inputMode, setInputMode]         = useState("text"); // "text" | "url"

  useEffect(() => {
    getModels().then(({ data }) => {
      setModels(data.models || []);
      setSelected((data.models || []).map((m) => m.modelId));
    });
    // Load preview info for chunk/URL status display
    getPromptPreview(filingId).then(({ data }) => setPreview(data)).catch(() => {});
  }, [filingId]);

  const startBenchmark = async () => {
    setPhase("running");
    const { data } = await runBenchmark({ filingId, models: selected, inputMode });
    setRunId(data.benchmarkRunId);
  };

  const pollRun = useCallback(() => {
    if (!runId) return;
    const interval = setInterval(async () => {
      const { data } = await getBenchmarkRun(runId);
      setRunData(data);
      if (data.status === "complete" || data.status === "partial") {
        clearInterval(interval);
        setPhase("complete");
      }
    }, 3000);
    return () => clearInterval(interval);
  }, [runId]);

  useEffect(() => { return pollRun(); }, [pollRun]);

  const ranked = runData?.summary?.ranked || [];
  const winner = ranked[0];

  const urlModeModels = selected.filter((id) => MODEL_META[id]?.urlMode);
  const textOnlyModels = selected.filter((id) => !MODEL_META[id]?.urlMode);

  return (
    <div style={{ maxWidth: 1000, margin: "0 auto" }}>
      <Title level={3} style={{ color: "#1A3C5E", marginBottom: 4 }}>AI Benchmark Lab</Title>
      <Text type="secondary" style={{ display: "block", marginBottom: 20 }}>
        Test multiple AI models against the same SEC filing — compare accuracy, speed, and cost.
      </Text>

      {/* ── Configure Phase ─────────────────────────────────────────── */}
      {phase === "configure" && (
        <Row gutter={[16, 16]}>
          <Col xs={24} md={16}>
            {/* Input Mode Toggle */}
            {preview?.urlModeAvailable && (
              <Card
                size="small"
                style={{ marginBottom: 16, borderColor: inputMode === "url" ? "#1677ff" : "#d9d9d9" }}
                title={<><LinkOutlined /> Input Mode</>}
              >
                <Space direction="vertical" style={{ width: "100%" }}>
                  <Segmented
                    block
                    value={inputMode}
                    onChange={setInputMode}
                    options={[
                      {
                        label: (
                          <div style={{ padding: "4px 0" }}>
                            <FileTextOutlined /> Extracted Text
                            {preview?.chunkCount > 1 && (
                              <Tag color="orange" style={{ marginLeft: 6, fontSize: 10 }}>
                                {preview.chunkCount} chunks
                              </Tag>
                            )}
                          </div>
                        ),
                        value: "text",
                      },
                      {
                        label: (
                          <div style={{ padding: "4px 0" }}>
                            <LinkOutlined /> Direct URL
                            <Tag color="green" style={{ marginLeft: 6, fontSize: 10 }}>GPT-4o + Gemini</Tag>
                          </div>
                        ),
                        value: "url",
                      },
                    ]}
                  />

                  {inputMode === "text" && preview?.chunkCount > 1 && (
                    <Alert
                      type="info" showIcon
                      icon={<SplitCellsOutlined />}
                      message={
                        <span>
                          <strong>Multi-pass chunking</strong> — filing is {(preview.rawTextChars/1000).toFixed(0)}k chars.
                          Each model will receive {preview.chunkCount} overlapping chunks of ~{(preview.chunkSize/1000).toFixed(0)}k chars
                          and results will be merged. All {(preview.filingTextChars/1000).toFixed(0)}k chars will be processed.
                        </span>
                      }
                    />
                  )}
                  {inputMode === "text" && preview?.chunkCount === 1 && (
                    <Alert type="success" showIcon
                      message={`Full filing fits in one pass (${(preview.filingTextChars/1000).toFixed(0)}k chars)`} />
                  )}
                  {inputMode === "url" && (
                    <Alert type="warning" showIcon
                      message={
                        <span>
                          <strong>URL mode:</strong> GPT-4o and Gemini will fetch the SEC document directly at
                          <Text code style={{ fontSize: 11, marginLeft: 4 }}>
                            {preview?.sourceUrl?.slice(0, 60)}…
                          </Text>
                          <br />
                          Other models ({textOnlyModels.join(", ")}) will still use chunked extracted text.
                        </span>
                      }
                    />
                  )}
                </Space>
              </Card>
            )}

            {/* Model Selection */}
            <Card title="Select Models to Benchmark">
              <Row gutter={[8, 8]}>
                {models.map((m) => {
                  const meta    = MODEL_META[m.modelId] || {};
                  const isSelected = selected.includes(m.modelId);
                  return (
                    <Col xs={24} sm={12} key={m.modelId}>
                      <Card
                        size="small"
                        onClick={() => setSelected((prev) =>
                          prev.includes(m.modelId) ? prev.filter((x) => x !== m.modelId) : [...prev, m.modelId]
                        )}
                        style={{
                          cursor: "pointer",
                          border: isSelected ? `2px solid ${meta.color || "#1677ff"}` : "1px solid #d9d9d9",
                          background: isSelected ? `${meta.color}08` : "#fff",
                          transition: "all 0.15s",
                        }}
                      >
                        <Space style={{ width: "100%", justifyContent: "space-between" }}>
                          <Space>
                            <span style={{ fontSize: 18 }}>{meta.emoji || "🤖"}</span>
                            <div>
                              <Text strong style={{ fontSize: 13 }}>{m.modelId.toUpperCase()}</Text>
                              <Space size={4} style={{ display: "block", marginTop: 2 }}>
                                <Tag color={TIER_COLORS[meta.tier] || "default"} style={{ fontSize: 10 }}>
                                  {meta.tier || "paid"}
                                </Tag>
                                {meta.urlMode && (
                                  <Tag color="cyan" style={{ fontSize: 10 }}>URL mode</Tag>
                                )}
                              </Space>
                              <Text type="secondary" style={{ fontSize: 11, display: "block" }}>
                                ${m.costPer1kInputTokens}/1K in · ${m.costPer1kOutputTokens}/1K out
                              </Text>
                            </div>
                          </Space>
                          {isSelected && <CheckOutlined style={{ color: meta.color || "#1677ff" }} />}
                        </Space>
                      </Card>
                    </Col>
                  );
                })}
              </Row>

              <Divider />

              <Space direction="vertical" style={{ width: "100%" }}>
                <Button
                  type="primary" size="large" icon={<RocketOutlined />}
                  disabled={selected.length === 0}
                  onClick={startBenchmark}
                  block
                  style={{ background: "#1A3C5E" }}
                >
                  Run Benchmark — {selected.length} Model{selected.length !== 1 ? "s" : ""}
                  {inputMode === "url" ? " (URL mode)" : preview?.chunkCount > 1 ? ` (${preview.chunkCount} chunks each)` : ""}
                </Button>
                <Button size="large" icon={<EyeOutlined />} onClick={() => setInspectorOpen(true)} block>
                  Inspect Prompt &amp; Extracted Text
                </Button>
              </Space>
            </Card>
          </Col>

          {/* Right panel */}
          <Col xs={24} md={8}>
            <Card title="What Gets Scored?" size="small">
              {[
                ["📊", "Income Statement", "Revenue, margins, EPS"],
                ["🏦", "Balance Sheet",    "Assets, liabilities, equity"],
                ["💸", "Cash Flows",       "Operating, investing, financing"],
                ["📈", "Key Ratios",       "ROE, P/E, liquidity, leverage"],
                ["📝", "MD&A Summary",     "AI-extracted narrative insights"],
                ["📆", "YoY Changes",      "Growth rates vs prior period"],
              ].map(([icon, label, desc]) => (
                <div key={label} style={{ marginBottom: 10 }}>
                  <Text strong style={{ fontSize: 13 }}>{icon} {label}</Text>
                  <br />
                  <Text type="secondary" style={{ fontSize: 11 }}>{desc}</Text>
                </div>
              ))}
            </Card>

            {preview && (
              <Card size="small" style={{ marginTop: 12 }} title="Filing Info">
                <Space direction="vertical" size={4} style={{ width: "100%" }}>
                  {preview.companyName && <Text strong>{preview.companyName}</Text>}
                  {preview.filingType  && <Tag color="blue">{preview.filingType}</Tag>}
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {(preview.rawTextChars / 1000).toFixed(0)}k total chars extracted
                  </Text>
                  {preview.chunkCount > 1 && (
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      → splits into {preview.chunkCount} chunks for full coverage
                    </Text>
                  )}
                  {preview.urlModeAvailable && (
                    <Tag color="green" icon={<LinkOutlined />} style={{ fontSize: 11 }}>EDGAR URL available</Tag>
                  )}
                </Space>
              </Card>
            )}
          </Col>
        </Row>
      )}

      {/* ── Running Phase ────────────────────────────────────────────── */}
      {phase === "running" && (
        <Card>
          <div style={{ textAlign: "center", padding: "32px 0" }}>
            <Spin size="large" />
            <Title level={4} style={{ marginTop: 16, color: "#1A3C5E" }}>Running Benchmark…</Title>
            <Text type="secondary">
              {inputMode === "url"
                ? "Models are fetching the SEC document directly from EDGAR."
                : preview?.chunkCount > 1
                  ? `Processing ${preview.chunkCount} chunks per model. Large filings take 30–120s.`
                  : "Parsing with all selected models in parallel."}
            </Text>
            {runData && (
              <div style={{ marginTop: 24 }}>
                {(runData.results || []).map((r) => {
                  const meta = MODEL_META[r.modelId] || {};
                  return (
                    <div key={r.modelId} style={{ marginBottom: 8, textAlign: "left", maxWidth: 400, margin: "8px auto" }}>
                      <Space style={{ width: "100%", justifyContent: "space-between" }}>
                        <Space>
                          <span>{meta.emoji || "🤖"}</span>
                          <Text style={{ fontSize: 13 }}>{r.modelId.toUpperCase()}</Text>
                        </Space>
                        <Tag color={
                          r.status === "complete" ? "green" :
                          r.status === "error"    ? "red"   :
                          r.status === "processing" ? "blue" : "default"
                        }>{r.status}</Tag>
                      </Space>
                      {r.status === "complete" && (
                        <Progress
                          percent={r.accuracyScore} size="small"
                          strokeColor={meta.color}
                          format={(p) => `${p}%`}
                          style={{ marginTop: 2 }}
                        />
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </Card>
      )}

      {/* ── Complete Phase ───────────────────────────────────────────── */}
      {phase === "complete" && runData && (
        <Space direction="vertical" style={{ width: "100%" }} size={16}>
          {winner && (
            <Alert
              type="success" showIcon
              icon={<TrophyOutlined />}
              message={
                <span>
                  <strong>{winner.modelId?.toUpperCase()}</strong> wins with{" "}
                  <strong>{winner.scoreReport?.accuracyScore}%</strong> accuracy
                  {inputMode === "url" && " (URL mode)"}
                </span>
              }
            />
          )}

          {/* Results table */}
          <Card title="Benchmark Results">
            <div style={{ overflowX: "auto" }}>
              <Table
                dataSource={ranked}
                rowKey="modelId"
                pagination={false}
                size="small"
                columns={[
                  {
                    title: "#", key: "rank",
                    render: (_, __, i) => (
                      <Text strong style={{ color: i === 0 ? "#faad14" : "#666" }}>
                        {i === 0 ? "🏆" : `#${i + 1}`}
                      </Text>
                    ),
                    width: 40,
                  },
                  {
                    title: "Model", dataIndex: "modelId",
                    render: (v) => {
                      const meta = MODEL_META[v] || {};
                      return (
                        <Space>
                          <span>{meta.emoji || "🤖"}</span>
                          <Text strong>{v?.toUpperCase()}</Text>
                          {meta.urlMode && inputMode === "url" && (
                            <Tag color="cyan" style={{ fontSize: 10 }}>URL</Tag>
                          )}
                        </Space>
                      );
                    },
                  },
                  {
                    title: "Score", dataIndex: ["scoreReport", "accuracyScore"],
                    render: (v, r) => {
                      const meta = MODEL_META[r.modelId] || {};
                      return (
                        <Space>
                          <Progress type="circle" percent={v} size={40}
                            strokeColor={meta.color} format={(p) => `${p}%`} />
                        </Space>
                      );
                    },
                    width: 80,
                  },
                  {
                    title: "Fields", key: "fields",
                    render: (_, r) => (
                      <Text style={{ fontSize: 12 }}>
                        {r.scoreReport?.fieldsPopulated ?? "—"} / {(r.scoreReport?.fieldsPopulated ?? 0) + (r.scoreReport?.fieldsMissing ?? 0)} filled
                      </Text>
                    ),
                  },
                  {
                    title: "Latency", dataIndex: "latencyMs",
                    render: (v) => v ? <Text style={{ fontSize: 12 }}>{(v/1000).toFixed(1)}s</Text> : "—",
                    width: 80,
                  },
                  {
                    title: "Cost", dataIndex: "estimatedCostUsd",
                    render: (v) => v ? <Text style={{ fontSize: 12 }}>${v.toFixed(5)}</Text> : "—",
                    width: 80,
                  },
                  {
                    title: "", key: "action",
                    render: (_, r) => (
                      <Button size="small" type="primary"
                        onClick={() => navigate(`/results/${runId}?model=${r.modelId}`)}
                        style={{ background: "#1A3C5E" }}>
                        View →
                      </Button>
                    ),
                  },
                ]}
              />
            </div>
          </Card>

          <Button onClick={() => { setPhase("configure"); setRunId(null); setRunData(null); }}>
            ← Run Again
          </Button>
        </Space>
      )}

      <PromptInspectorModal filingId={filingId} open={inspectorOpen} onClose={() => setInspectorOpen(false)} />
    </div>
  );
}